/** Expanded game detail card shown when a bar is tapped (team variant) */

import { teamLineOutcome } from '@parlay-picker/shared/lineOutcome';
import { cn } from '@utils/cn';
import { motion } from 'framer-motion';
import type { ReactElement } from 'react';
import { OUTCOME_STYLE } from '../stats-chart/outcomeStyles';
import type { ChartDataPoint } from './types';

interface TeamGameDetailCardProps {
  point: ChartDataPoint;
  propType: string;
  propLine: number;
  isUnderBet: boolean;
  teamName?: string;
  onClose: () => void;
}

export function TeamGameDetailCard({
  point,
  propType,
  propLine,
  isUnderBet,
  teamName,
  onClose,
}: TeamGameDetailCardProps): ReactElement {
  const outcome = teamLineOutcome(point, propType, propLine, isUnderBet);
  const teamShort = teamName?.split(' ').pop() || 'Team';

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      className="overflow-hidden"
    >
      <div
        className={cn(
          'flex items-center justify-between rounded-lg px-3 py-2 mt-2 border cursor-pointer',
          OUTCOME_STYLE[outcome].card,
        )}
        onClick={onClose}
      >
        <div className="flex items-center gap-3">
          <div>
            <div className="text-xs text-theme-text-tertiary">
              {point.dateFormatted || point.label}
            </div>
            <div className="text-xs text-theme-text-tertiary/70">
              {point.isHome ? 'vs' : '@'} {point.opponent}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-bold text-theme-text-secondary">
            {teamShort} {point.teamScore} - {point.opponentScore}
          </span>
          <span className={cn('text-xs font-semibold', OUTCOME_STYLE[outcome].text)}>
            {propType === 'total'
              ? `Total: ${point.teamScore + point.opponentScore}`
              : point.value > 0
                ? `+${point.value}`
                : point.value}
          </span>
        </div>
      </div>
    </motion.div>
  );
}
